import { Anchor, RingProgress } from '@mantine/core'
import { Link } from 'react-router-dom'
import dayjs from 'dayjs'
import Badge from '../ui/Badge'
import Box from '../ui/Box'

function PurchaseOrderLine({ line, orderedComponent }) {
    const categoryName = orderedComponent.component_category?.name_fr ?? orderedComponent.component_category?.name
    const componentName = [categoryName, orderedComponent.name].filter(Boolean).join(' — ')
    const received = Number(line.quantity_received ?? 0)
    const remaining = Number(line.quantity_remaining)
    const total = Number(line.quantity_to_receive ?? received + remaining)
    const progress = total > 0 ? Math.min(100, Math.max(0, received / total * 100)) : 0
    const date = line.reception_at && dayjs(line.reception_at).isValid()
        ? `Réception le ${dayjs(line.reception_at).format('DD/MM/YYYY')}`
        : 'Date de réception non précisée'

    return <div className="flex flex-wrap items-center justify-between gap-2 px-3 py-1.5">
        <div className="flex min-w-0 flex-1 items-center gap-2">
            <div className="shrink-0">
                <RingProgress size={24} thickness={4}
                    sections={[{ value: progress, color: 'green', tooltip: `${received.toLocaleString('fr-FR')} reçus` }]} />
            </div>
            <div className="flex min-w-0 flex-1 flex-col items-start">
                <span className="text-sm leading-5 break-words" title={componentName}>{componentName}</span>
                <span className="text-xs leading-4 text-gray-500">
                    {date}
                    {received > 0 && <span className="ml-2 text-green-600">{received.toLocaleString('fr-FR')} reçus</span>}
                </span>
            </div>
        </div>
        <div className="ml-auto shrink-0">
            <Badge size="xs" color="pink">{remaining.toLocaleString('fr-FR')} à recevoir</Badge>
        </div>
    </div>
}

function PurchaseOrderCard({ items }) {
    const { line } = items[0]
    const order = line.purchase_order

    return <Link to={`/purchase_orders/${line.purchase_order_id}`}
        className="rounded-xl border hover:bg-gray-50 focus-visible:outline-2 focus-visible:outline-baltic">
        <div className="flex flex-wrap items-center justify-between gap-x-3 gap-y-1 border-b px-3 py-2">
            <div className="flex min-w-0 flex-1 flex-wrap items-baseline gap-x-2 gap-y-0.5">
                <Anchor component="span" className="max-w-full truncate text-sm" title={order.reference || undefined}>
                    BC{line.purchase_order_id}{order.reference ? ` - ${order.reference}` : ''}
                </Anchor>
                <span className="max-w-full truncate text-xs text-gray-500" title={order.supplier?.name}>
                    {order.supplier?.name || 'Fournisseur non renseigné'}
                </span>
            </div>
            <span className="text-xs text-gray-500">{order.location?.name || 'Lieu non renseigné'}</span>
        </div>
        <div className="divide-y">
            {items.map(({ line, orderedComponent }) => (
                <PurchaseOrderLine key={line.id} line={line} orderedComponent={orderedComponent} />
            ))}
        </div>
    </Link>
}

function pendingOrders(components) {
    const orders = new Map()
    for (const item of pendingLines(components)) {
        const orderId = item.line.purchase_order_id
        if (!orders.has(orderId)) orders.set(orderId, { id: orderId, items: [] })
        orders.get(orderId).items.push(item)
    }
    return [...orders.values()]
}

function pendingLines(components) {
    const seen = new Set()
    return components.flatMap((component) => (component.purchase_order_line_items ?? [])
        .filter((line) => {
            if (seen.has(line.id) || Number(line.quantity_remaining) <= 0
                || line.purchase_order?.status !== 'open' || line.purchase_order?.canceled_at) return false
            seen.add(line.id)
            return true
        })
        .map((line) => ({ line, orderedComponent: component })))
        .sort((a, b) => (a.line.reception_at || '9999').localeCompare(b.line.reception_at || '9999')
            || a.line.purchase_order_id - b.line.purchase_order_id || a.line.id - b.line.id)
}

export default function ComponentPurchaseOrders({ component }) {
    const groups = [
        { title: 'Bons de commande', subtitle: 'Ce composant, à réceptionner', items: pendingOrders([component]) },
        { title: 'Bons de commandes liés', subtitle: 'Réceptions de composants enfants', items: pendingOrders(component.children ?? []) },
    ]

    return <>{groups.filter((group) => group.items.length > 0).map((group) => (
        <Box key={group.title} title={group.title} subtitle={group.subtitle} maxHeight="400px">
            <div className="flex flex-col gap-2">
                {group.items.map(({ id, items }) => (
                    <PurchaseOrderCard key={id} items={items} />
                ))}
            </div>
        </Box>
    ))}</>
}
